import { useContext, useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import ModalBasicLayout from "../ModalBasicLayout";
import {
  ChatSidebar,
  ResponsiveSidebarContainer,
} from "../ModalImageEdit/shared";
import InstanceCanvas from "./InstanceCanvas";
import InstanceLayersSidebar from "./InstanceLayersSidebar";
import InstancePropertiesPanel from "./InstancePropertiesPanel";
import useTemplateInstance from "../../../features/creativeTemplates/instance/useTemplateInstance";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { getKeyDatabase } from "../../../services/public";
import { startSaveEditedProduceCreative } from "../../../actions/creativeTemplatesProduce";
import {
  startListenerMergedFonts,
  stopListenerMergedFonts,
} from "../../../actions/fonts";
import { loadFontFaces } from "../../../features/creativeTemplates/utils/fonts";
import { UploadFilesContext } from "../../../contexts/UploadFilesContext";
import { SimpleAlert } from "../../Alerts/Alerts";
import {
  TEMPLATES_NEW_LAYER,
  TEMPLATES_PRODUCE_ADD_GROUP,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_SHAPE,
  TEMPLATES_PRODUCE_ADD_TEXT,
  TEMPLATES_PRODUCE_EDIT_TITLE,
  TEMPLATES_PRODUCE_SAVE_ERROR,
} from "../../../i18n/keysTranslations";

const isNativeEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
    return true;
  }
  return target.isContentEditable;
};

const ModalCreativeTemplateEdit = ({
  isOpen,
  onClose,
  produceID,
  item,
}) => {
  const theme = useTheme();
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { uploadFile } = useContext(UploadFilesContext) || {};
  const { userID } = dispatch(getPathDatabaseByRole());
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const fontListenerRefs = useRef({ commonRef: null, advertiserRef: null });
  const instance = item?.instance || null;

  const {
    width,
    height,
    nodes,
    nodeLayouts,
    items,
    selectedID,
    selectedItem,
    editingTextID,
    expandedIDs,
    setSelectedID,
    setEditingTextID,
    setExpandedIDs,
    updateBox,
    patchSelected,
    renameLayer,
    setTextValue,
    addLayer,
    deleteLayer,
    reorderSiblings,
    replaceImage,
    undo,
    redo,
    endCoalesce,
    getPersistInstance,
  } = useTemplateInstance({
    instance,
    enabled: isOpen && Boolean(instance),
  });

  useEffect(() => {
    if (!isOpen) return undefined;
    setIsSidebarOpen(true);
    dispatch(
      startListenerMergedFonts({
        onUpdatedDataCallback: (fonts) => {
          loadFontFaces({ fonts });
        },
        onReferenceAvailable: (refs) => {
          fontListenerRefs.current = refs;
        },
      })
    );
    return () => {
      dispatch(
        stopListenerMergedFonts({ listenerRefs: fontListenerRefs.current })
      );
    };
  }, [dispatch, isOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isOpen) return;
      if (isNativeEditableTarget(event.target)) return;
      const isUndo =
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        event.key?.toLowerCase() === "z";
      const isRedo =
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key?.toLowerCase() === "z";
      if (isUndo) {
        event.preventDefault();
        undo();
        return;
      }
      if (isRedo) {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, redo, undo]);

  const onChangeField = ({ key, value }) => {
    const node = selectedItem?.node;
    if (!node) return;
    switch (key) {
      case "name":
        patchSelected({ patch: { name: value } });
        return;
      case "fixedValue":
        setTextValue(selectedID, value);
        return;
      case "fontAssetRef":
      case "fontSize":
      case "color":
      case "align":
      case "orientation":
        patchSelected({
          patch: {
            style: {
              ...(node.style || {}),
              [key]: value,
            },
          },
        });
        return;
      case "fill":
      case "radius":
        patchSelected({
          patch: {
            shape: {
              ...(node.shape || { kind: "roundedRectangle" }),
              [key]: value,
            },
          },
        });
        return;
      default: {
        const exhaustive = key;
        throw new Error(`Unhandled property: ${exhaustive}`);
      }
    }
  };

  const onChangeBox = ({ x, y, width: nextWidth, height: nextHeight }) => {
    if (!selectedItem || !width || !height) return;
    const box = selectedItem.box;
    updateBox({
      id: selectedID,
      nx: (x ?? box.x) / width,
      ny: (y ?? box.y) / height,
      nw: (nextWidth ?? box.width) / width,
      nh: (nextHeight ?? box.height) / height,
    });
    endCoalesce();
  };

  const onAddLayer = ({ type, url }) => {
    const names = {
      text: t(TEMPLATES_PRODUCE_ADD_TEXT),
      image: t(TEMPLATES_PRODUCE_ADD_IMAGE),
      shape: t(TEMPLATES_PRODUCE_ADD_SHAPE),
      group: t(TEMPLATES_PRODUCE_ADD_GROUP),
    };
    addLayer({
      type,
      url,
      name: names[type] || t(TEMPLATES_NEW_LAYER),
      id: dispatch(getKeyDatabase()),
    });
  };

  const onUploadAsset = async ({ file }) => {
    if (!file || typeof uploadFile !== "function" || !selectedID) return;
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (uploadedUrl) {
        replaceImage({
          nodeID: selectedID,
          url: uploadedUrl,
          name: selectedItem?.node?.name || selectedID,
        });
      }
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const onSave = async () => {
    if (!produceID || !item?.id || isSaving) return;
    setIsSaving(true);
    try {
      const result = await dispatch(
        startSaveEditedProduceCreative({
          produceID,
          creativeID: item.id,
          instance: getPersistInstance(),
        })
      );
      if (!result?.ok) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
          icon: "error",
        });
        throw new Error(result?.message || "Failed to save edited creative");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onTextCommit = (nodeID, value) => {
    setTextValue(nodeID, value);
    setEditingTextID(null);
    endCoalesce();
  };

  return (
    <ModalBasicLayout
      modalOpen={isOpen}
      onCloseModal={onClose}
      fullScreen={true}
      maxWidth={false}
      fullWidth={true}
      hasTitle={false}
      ariaLabel={t(TEMPLATES_PRODUCE_EDIT_TITLE)}
      sxContent={{
        py: 0,
        px: 0,
        display: "flex",
        flexDirection: "row",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
      sxPaper={{
        height: "100vh",
        maxHeight: "100vh",
      }}
    >
      <ResponsiveSidebarContainer
        isSmallScreen={isSmallScreen}
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      >
        {isSidebarOpen ? (
          <ChatSidebar
            isSmallScreen={isSmallScreen}
            title={t(TEMPLATES_PRODUCE_EDIT_TITLE)}
            onToggleSidebar={() => setIsSidebarOpen(false)}
            hideInput
          >
            <Box
              sx={{
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <InstanceLayersSidebar
                nodes={nodes}
                nodeLayouts={nodeLayouts}
                items={items}
                selectedID={selectedID}
                expandedIDs={expandedIDs}
                onSelectLayer={setSelectedID}
                onRenameLayer={renameLayer}
                onToggleExpand={(id) =>
                  setExpandedIDs((current) => ({
                    ...current,
                    [id]: !current[id],
                  }))
                }
                onReorderSiblings={reorderSiblings}
                onAddLayer={onAddLayer}
                onDeleteLayer={deleteLayer}
              />
            </Box>
          </ChatSidebar>
        ) : null}
      </ResponsiveSidebarContainer>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          overflow: "hidden",
        }}
      >
        <InstanceCanvas
          items={items}
          canvasWidth={width}
          canvasHeight={height}
          selectedLayerId={selectedID}
          editingTextID={editingTextID}
          isSidebarOpen={isSidebarOpen}
          previewUrl={item?.url || ""}
          onToggleSidebar={() => setIsSidebarOpen(true)}
          onSelectLayer={setSelectedID}
          onLayerBoxChange={updateBox}
          onStartTextEdit={setEditingTextID}
          onTextCommit={onTextCommit}
          onClose={onClose}
          onCallbackSave={onSave}
          saveDisabled={false}
        />
        <Box
          sx={{
            width: { xs: "100%", md: 320 },
            maxWidth: { xs: "100%", md: 320 },
            height: { xs: "auto", md: "100%" },
            maxHeight: { xs: "40%", md: "none" },
            flexShrink: 0,
            bgcolor: "background.paper",
            borderLeft: { xs: 0, md: "1px solid rgba(0, 0, 0, 0.12)" },
            borderTop: { xs: "1px solid rgba(0, 0, 0, 0.12)", md: 0 },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <InstancePropertiesPanel
            selectedItem={selectedItem}
            isUploadingAsset={isUploadingAsset}
            onChangeField={onChangeField}
            onChangeBox={onChangeBox}
            onUploadAsset={onUploadAsset}
          />
        </Box>
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalCreativeTemplateEdit;
